<!--
 * @Author: LiZhiWei
 * @Date: 2026-08-12 15:00:00
 * @Description: 内页头 Inner Hero - 面包屑 + H1 + 说明，对齐高保真原型
-->
<template>
  <section class="inner-hero" :data-od-id="id">
    <template v-if="hasBanners">
      <div
        v-for="(banner, index) in banners"
        :key="banner.id"
        class="inner-hero-slide"
        :class="{ active: index === currentIndex }"
        :aria-hidden="index === currentIndex ? undefined : true"
      >
        <picture aria-hidden="true">
          <source
            v-if="banner.mobile_image_url"
            :srcset="banner.mobile_image_url"
            media="(max-width: 767px)"
          />
          <img :src="banner.image_url" :alt="banner.image_alt || ''" />
        </picture>
      </div>
    </template>
    <picture v-if="!hasBanners && backgroundImage && !backgroundFailed" aria-hidden="true">
      <source
        v-if="mobileBackgroundImage"
        :srcset="mobileBackgroundImage"
        media="(max-width: 767px)"
      />
      <img :src="backgroundImage" alt="" @error="backgroundFailed = true" />
    </picture>
    <div v-if="banners.length > 1" class="inner-hero-arrows" role="group" aria-label="切换 Banner">
      <button
        class="inner-hero-arrow"
        type="button"
        aria-label="上一张 Banner"
        title="上一张"
        @click="handleSlide(currentIndex - 1, $event)"
      >
        <i class="i-lucide-chevron-left" aria-hidden="true"></i>
      </button>
      <button
        class="inner-hero-arrow"
        type="button"
        aria-label="下一张 Banner"
        title="下一张"
        @click="handleSlide(currentIndex + 1, $event)"
      >
        <i class="i-lucide-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
    <div class="inner-hero-content page-container">
      <slot>
        <nav v-if="showBreadcrumb" class="breadcrumb" aria-label="面包屑">
          <nuxt-link to="/">首页</nuxt-link>
          <span aria-hidden="true">/</span>
          <nuxt-link v-if="parentLabel && parentTo" :to="parentTo">{{ parentLabel }}</nuxt-link>
          <span v-else-if="parentLabel">{{ parentLabel }}</span>
          <span v-if="parentLabel" aria-hidden="true">/</span>
          <span aria-current="page">{{ title }}</span>
        </nav>
        <h1>{{ title }}</h1>
        <p v-if="description">{{ description }}</p>
      </slot>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { BannerItem } from '~/types/site-api'

  /**
   * 内页头组件 Props
   */
  interface InnerHeroProps {
    /** 装饰性背景图片，可选 */
    backgroundImage?: string
    /** 轮播 banner 列表，多张时自动切换 */
    banners?: BannerItem[]
    /** 栏目说明文字，可选 */
    description?: string
    /** 锚点 ID，可选 */
    id?: string
    /** 移动端装饰性背景图片，可选 */
    mobileBackgroundImage?: string
    /** 父栏目名称（如"企业概况"），可选 */
    parentLabel?: string
    /** 父栏目地址，可选 */
    parentTo?: string
    /** 是否显示 Hero 内的面包屑 */
    showBreadcrumb?: boolean
    /** 页面标题（同时作为面包屑当前项） */
    title: string
  }

  defineOptions({
    name: 'InnerHero',
  })

  const props = withDefaults(defineProps<InnerHeroProps>(), {
    banners: () => [],
    backgroundImage: undefined,
    description: undefined,
    id: 'inner-hero',
    mobileBackgroundImage: undefined,
    parentLabel: undefined,
    parentTo: undefined,
    showBreadcrumb: true,
  })

  const backgroundFailed = shallowRef(false)

  watch(
    () => [props.backgroundImage, props.mobileBackgroundImage],
    () => {
      backgroundFailed.value = false
    }
  )

  /** banner 轮播：多张时按固定间隔自动切换，单张或无数据时保持静态。 */
  const hasBanners = computed(() => props.banners.length > 0)
  const currentIndex = shallowRef(0)
  const reducedMotion = shallowRef(false)
  let timer: ReturnType<typeof setInterval> | undefined

  function clearTimer() {
    if (timer) clearInterval(timer)
    timer = undefined
  }

  function startTimer() {
    clearTimer()
    if (reducedMotion.value || props.banners.length < 2) return
    timer = setInterval(() => {
      currentIndex.value = (currentIndex.value + 1) % props.banners.length
    }, 6000)
  }

  function setSlide(index: number) {
    currentIndex.value = (index + props.banners.length) % props.banners.length
    startTimer()
  }

  function handleSlide(index: number, event: MouseEvent) {
    setSlide(index)

    const target = event.currentTarget
    if (event.detail > 0 && target instanceof HTMLElement) target.blur()
  }

  watch(
    () => props.banners,
    () => {
      if (currentIndex.value >= props.banners.length) currentIndex.value = 0
      startTimer()
    }
  )

  onMounted(() => {
    reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    startTimer()
  })

  onUnmounted(clearTimer)
</script>

<style scoped lang="scss">
  // 组件样式在 inner-page.scss 中统一定义
</style>
